import { Injectable, Injector } from '@angular/core';

import { IDynamicInjectorProvider } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { ClientConfigProductName, ProductMetadata, ProductService } from '@frontend/vanilla/core';
import { BehaviorSubject, Observable, distinctUntilChanged, of } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SportsProductService implements IDynamicInjectorProvider {
    readonly isSportsProductActive$: Observable<boolean>;

    constructor(
        private productService: ProductService,
        private injector: Injector,
    ) {
        if (!productService.isSingleDomainApp) {
            this.isSportsProductActive$ = of(true);

            return;
        }

        const subject$ = new BehaviorSubject(this.checkIsSportsProduct(productService.current));

        productService.productChanged.subscribe((data) => {
            subject$.next(this.checkIsSportsProduct(data));
        });

        this.isSportsProductActive$ = subject$.asObservable().pipe(distinctUntilChanged());
    }

    getDynamicInjector(): Injector {
        if (!this.productService.isSingleDomainApp) {
            return this.injector;
        }

        const currentInjector = this.productService.current.injector;

        const sportsProduct = this.productService.getMetadata(ClientConfigProductName.SPORTS);

        if (!sportsProduct.isRegistered) {
            console.error(`Attempted to use sports product injector but sports product was not registered.`);

            return currentInjector;
        }

        return sportsProduct.injector;
    }

    activateSports(): void {
        this.productService.setActive(ClientConfigProductName.SPORTS);
    }

    deactivateSports(): void {
        this.productService.deactivateProduct(ClientConfigProductName.SPORTS);
    }

    get isSingleDomain(): boolean {
        return this.productService.isSingleDomainApp;
    }

    get sportsProductName(): string {
        return ClientConfigProductName.SPORTS;
    }

    private checkIsSportsProduct(metadata: ProductMetadata): boolean {
        return metadata.name === ClientConfigProductName.SPORTS;
    }
}
